import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  Bell,
  BellRing,
  CheckCheck,
  FileCheck2,
  FileText,
  RotateCcw,
  Send,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { NotificationContext } from "./NotificationProvider";
import "./NotificationBell.css";

const getNotificationPresentation = (eventType = "") => {
  const type = String(eventType).toUpperCase();
  if (type.includes("ARCHIV")) return { tone: "archive", Icon: Archive };
  if (type.includes("REVIEW") || type.includes("RETURN") || type.includes("RESUB")) return { tone: "warning", Icon: RotateCcw };
  if (type.includes("APPROV") || type.includes("ACTION")) return { tone: "success", Icon: FileCheck2 };
  if (type.includes("FORWARD") || type.includes("ROUTE") || type.includes("UPLOAD")) return { tone: "info", Icon: Send };
  return { tone: "neutral", Icon: FileText };
};

const formatRelativeTime = (value) => {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "Just now";
  const seconds = Math.round((timestamp - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const ranges = [[60, "second"], [60, "minute"], [24, "hour"], [7, "day"]];
  let amount = seconds;
  for (const [limit, unit] of ranges) {
    if (Math.abs(amount) < limit) return formatter.format(Math.round(amount), unit);
    amount /= limit;
  }
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const NotificationBell = () => {
  const {
    notifications,
    unreadCount,
    isRead,
    markRead,
    markAllRead,
    dismissNotification,
    browserPermission,
    enableBrowserNotifications,
    soundEnabled,
    toggleSound,
  } = useContext(NotificationContext);
  const [isOpen, setIsOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const bellRef = useRef(null);
  const previousUnreadRef = useRef(unreadCount);

  useEffect(() => {
    if (unreadCount > previousUnreadRef.current) {
      setShake(true);
      const timer = window.setTimeout(() => setShake(false), 700);
      previousUnreadRef.current = unreadCount;
      return () => window.clearTimeout(timer);
    }
    previousUnreadRef.current = unreadCount;
    return undefined;
  }, [unreadCount]);

  useEffect(() => {
    const closeOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const visibleNotifications = useMemo(() => notifications.slice(0, 50), [notifications]);

  const openNotification = async (notification) => {
    await markRead(notification.id);
    const fileId = notification.payload?.fileId;
    if (fileId) {
      window.dispatchEvent(new CustomEvent("wavey:open-document", { detail: { fileId, notification } }));
      setIsOpen(false);
    }
  };

  return (
    <div className="notification-centre" ref={bellRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`notification-centre__trigger ${shake ? "notification-centre__trigger--shake" : ""}`}
        aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : "Notifications"}
        aria-expanded={isOpen}
      >
        {unreadCount ? <BellRing size={21} /> : <Bell size={21} />}
        {unreadCount > 0 && <span className="notification-centre__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>

      {isOpen && (
        <section className="notification-panel" aria-label="Notification centre">
          <header className="notification-panel__header">
            <div><span>Updates</span><h3>Notifications</h3></div>
            {unreadCount > 0 && (
              <button type="button" onClick={markAllRead}><CheckCheck size={16} /> Mark all read</button>
            )}
          </header>

          <div className="notification-panel__preferences">
            {browserPermission !== "granted" && browserPermission !== "unsupported" && (
              <button type="button" onClick={enableBrowserNotifications}><Bell size={15} /> Enable desktop alerts</button>
            )}
            <button type="button" onClick={toggleSound}>
              {soundEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
              Sound {soundEnabled ? "on" : "off"}
            </button>
          </div>

          <div className="notification-panel__list">
            {visibleNotifications.length === 0 ? (
              <div className="notification-panel__empty">
                <Bell size={25} />
                <strong>You are all caught up</strong>
                <span>Document activity relevant to your appointment will appear here.</span>
              </div>
            ) : visibleNotifications.map((notification) => {
              const { tone, Icon } = getNotificationPresentation(notification.eventType);
              const unread = !isRead(notification);
              return (
                <article key={notification.id} className={`notification-item ${unread ? "notification-item--unread" : ""}`}>
                  <button type="button" className="notification-item__body" onClick={() => openNotification(notification)}>
                    <span className={`notification-item__icon notification-item__icon--${tone}`}><Icon size={17} /></span>
                    <span className="notification-item__content">
                      <span className="notification-item__title-row">
                        <strong>{notification.title || "Document update"}</strong>
                        {unread && <i aria-label="Unread" />}
                      </span>
                      <span className="notification-item__message">{notification.message}</span>
                      <span className="notification-item__meta">
                        {formatRelativeTime(notification.createdAt)}
                        {notification.payload?.actorRole ? ` • ${notification.payload.actorRole}` : ""}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="notification-item__dismiss"
                    onClick={() => dismissNotification(notification.id)}
                    aria-label="Dismiss notification"
                    title="Dismiss"
                  ><X size={15} /></button>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationBell;
